import React, { useEffect, useState, useRef, useMemo } from "react";

// STYLES
import "./GoPremiumLander.scss";

// STATIC
import headerIcon from "../../../../static/img/img-ihr-by-bodymatter-hr-logo-black-sm@3x.png";
import heroImage from "../../../../static/img/img-finger-scan-lm@3x.png";
import ihrLogo from "../../../../static/img/img-ihr-name-logo-black.svg";

import { ReactComponent as ProfileIcon } from "../../icons/profile.svg";

// HOOKS
import { useAuthContext } from "../../../../hooks/context/AuthContext";
import { useParams } from "react-router-dom";
import { useNavigate, useSearchParams } from "react-router-dom";

// COMPONENTS
import CheckoutCardForm from "../../inputs/CheckoutCardForm";
import LoadingSpinner from "../../modals/Spinners/LoadingSpinner";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import PremiumCalloutSummary from "../../callouts/PremiumCalloutSummary";

// ANALYTICS
import { analyticsSetUser, analyticsTrack } from "../../../../utils/amplitude";

// IMPORTANT - this must be at module level (see docs)
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);
const priceId = process.env.REACT_APP_STRIPE_5999_YEARLY_PRICE;
const STRIPE_PRICE_TO_PRODUCT: Record<string, string> = {
  // YEARLY
  [process.env.REACT_APP_STRIPE_5999_YEARLY_PRICE!]:
    "io.bodymatter.premium.instantheartratefree.1y.60usd.20250501",
};

// APPROVED VARIANTS
const APPROVED_VARIANTS = new Set<string>([
  "go_premium_default",
  "the_new_ihr_1",
  "the_new_ihr_2",
  "the_new_ihr_1_experiment", // testing default IP pool
  "discover_heart_score_1",
  "discover_heart_age_1",
  "discover_heart_age_2",
  "loyalty_offer_1",
]);
const DEFAULT_VARIANT = "go_premium_default";

const GoPremiumLander: React.FC = () => {
  const navigate = useNavigate();
  const { user_id } = useParams<{ user_id: string }>();
  const [showPaymentSpinner, setShowPaymentSpinner] = useState<boolean>(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const userId = user_id ?? "";
  const viewedEventSentRef = useRef(false);

  const {
    token,
    eligibility,
    fetchEligibility,
    postSubscriptionsCreate,
    getStripeSubscriptionInfo,
    isPremium,
    email: acctEmail,
  } = useAuthContext();

  const [searchParams] = useSearchParams();

  const { promoPageVariant, isVariantApproved } = useMemo(() => {
    const raw = (
      searchParams.get("offer_variant") || // new
      searchParams.get("v") || // old
      ""
    )
      .trim()
      .toLowerCase();

    const safe = raw.replace(/[^a-z0-9_-]/g, "");

    if (APPROVED_VARIANTS.has(safe)) {
      return { promoPageVariant: safe, isVariantApproved: true };
    }

    return { promoPageVariant: DEFAULT_VARIANT, isVariantApproved: false };
  }, [searchParams]);

  const redirectReason = useMemo(() => {
    const raw = (searchParams.get("redirect_reason") || "").trim();
    return raw.length ? raw : null;
  }, [searchParams]);

  const redirectBanner = useMemo(() => {
    if (!redirectReason) return null;

    if (redirectReason === "loyalty_offer_expired") {
      return {
        line1: "Your loyalty offer has expired.",
        line2: "Don’t worry, you can still upgrade to Premium below.",
      };
    }

    return {
      line1: " ",
      line2: "Don’t worry, you can still upgrade to Premium below.",
    };
  }, [redirectReason]);

  const emailDisplay = useMemo<string>(() => {
    if (!isVariantApproved) return " ";

    const rawParam =
      searchParams.get("user_email") || // new
      searchParams.get("email"); // old

    if (!rawParam) return " ";

    const raw = decodeURIComponent(String(rawParam)).trim();
    if (!raw || raw === "null" || raw === "undefined") return " ";

    const at = raw.indexOf("@");
    if (at <= 0 || at === raw.length - 1) return " ";

    const local = raw.slice(0, at);
    const domain = raw.slice(at + 1);

    const maskedLocal = local.length > 10 ? `${local.slice(0, 10)}***` : local;
    return `${maskedLocal}@${domain}`.slice(0, 160);
  }, [searchParams, isVariantApproved]);

  // push content down a bit so the fixed banner doesn’t cover the headline
  const topPush = redirectBanner ? 72 : 0;
  const contentOffsetStyle: React.CSSProperties | undefined = topPush
    ? { paddingTop: topPush }
    : undefined;

  useEffect(() => {
    if (!userId || eligibility != null) return;
    fetchEligibility(userId);
  }, [userId, fetchEligibility, eligibility]);

  useEffect(() => {
    if (eligibility === true && !viewedEventSentRef.current) {
      viewedEventSentRef.current = true;
      if (userId) analyticsSetUser(userId);
      analyticsTrack("goPremiumWebPageViewed", {
        path: window.location.pathname,
        ts: Date.now(),
        promoPageVariant: promoPageVariant,
        fullQuery: window.location.search,
      });
    }
  }, [eligibility, userId, promoPageVariant]);

  useEffect(() => {
    if (eligibility === false) {
      navigate("/already-premium/", { replace: true });
    }
  }, [eligibility, isPremium, navigate]);

  return (
    <section className="GoPremiumLander" aria-label="Go Premium">
      {emailDisplay && (
        <div
          className="GoPremiumLander__emailBanner"
          role="note"
          aria-label="Email"
        >
          <img src={ihrLogo} alt="Instant Heart Rate" className="brand" />
          <div className="right">
            <ProfileIcon className="icon" />
            <span className="emailText">{emailDisplay}</span>
          </div>
        </div>
      )}

      {redirectBanner && (
        <div className="GoPremiumLander__redirectBanner" role="alert">
          <span className="GoPremiumLander__redirectBannerLine1">
            {redirectBanner.line1}
          </span>{" "}
          <span className="GoPremiumLander__redirectBannerLine2">
            {redirectBanner.line2}
          </span>
        </div>
      )}

      <LoadingSpinner
        className="GoPremiumLander__opaqueSpinner"
        raised={showPaymentSpinner}
        fullPage
      />

      <div className="GoPremiumLander__content" style={contentOffsetStyle}>
        <div className="GoPremiumLander__contentWrapper">
          <h1 className="HRVCallout">
            Get the Big Picture
            <br />
            of Your Heart Health
          </h1>
          <p className="HRVSubheader">Unlock now with Premium for iPhone</p>

          <div className="GoPremiumLander__heroWithSummary">
            <img
              src={heroImage}
              loading="lazy"
              alt="Instant Heart Rate"
              className="heroImage"
            />
            <div className="OrderSummaryOverlay">
              <div className="trialTab">Free 3-Day Trial</div>
              <div className="OrderSummaryWrapper">
                <PremiumCalloutSummary
                  copyTextUpper="$59.99 USD / year"
                  copyTextLower="Charged after trial. Cancel anytime."
                />
              </div>
            </div>
          </div>

          <p className="GoPremiumLander__platform-disclaimer">
            This offer only valid for Apple devices. 3 day free trial available
            only to first-time subscribers. Subscription will automatically
            renew unless cancelled before the end of current period. Manage your
            subscription from reports.instantheartrate.com. Heart Age available
            only on supported devices. By proceeding, you agree to the{" "}
            <a href="https://instantheartrate.com/privacy-policy-and-terms-of-use#tos-section">
              Terms
            </a>{" "}
            and{" "}
            <a href="https://instantheartrate.com/privacy-policy-and-terms-of-use#privacy-policy-section">
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>

      <div className="GoPremiumLander__checkout" style={contentOffsetStyle}>
        <div className="GoPremiumLander__checkoutWrapper">
          {eligibility ? (
            <Elements stripe={stripePromise} options={{ locale: "auto" }}>
              <CheckoutCardForm
                email={acctEmail ?? ""}
                userId={userId}
                submissionErrorText={
                  paymentFailed ? "Payment failed, please try again later." : ""
                }
                onPaymentMethodCreated={(pm) => {
                  setShowPaymentSpinner(true);

                  const onSuccess = () => {
                    setPaymentFailed(false);

                    const productID =
                      (priceId && STRIPE_PRICE_TO_PRODUCT[priceId]) ||
                      undefined;

                    if (userId) {
                      analyticsSetUser(userId);
                    }

                    if (productID && userId) {
                      analyticsTrack("boughtIAP", {
                        productID,
                        path: window.location.pathname,
                        ts: Date.now(),
                        promoPageVariant,
                        fullQuery: window.location.search,
                      });
                    }

                    navigate("/checkout-success/", { replace: true });
                    setShowPaymentSpinner(false);
                    getStripeSubscriptionInfo(token!!);
                  };

                  const onFail = (error?: string) => {
                    if (error === "user is not eligible") {
                      navigate("/already-premium", { replace: true });
                    }
                    setPaymentFailed(true);
                    setShowPaymentSpinner(false);
                  };

                  if (priceId) {
                    postSubscriptionsCreate(
                      userId,
                      priceId,
                      pm.id,
                      onSuccess,
                      onFail,
                    );
                  }
                }}
              />
            </Elements>
          ) : eligibility === null ? (
            <div className="GoPremiumLander__loading">Loading...</div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default GoPremiumLander;
